import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Globe2, CreditCard, Languages, Clock, FileSignature, ArrowRight, Building2 } from 'lucide-react';
import { plansAPI } from '@/utils/api';
import { EurHint } from '@/components/EurHint';

const COPY = {
  pt: {
    eyebrow: 'Empresas internacionais e da diáspora',
    title: 'Contrate talento cabo-verdiano à distância.',
    lead: 'Publique vagas de trabalho remoto ou prestação de serviços e receba candidaturas de profissionais em Cabo Verde.',
    cta: 'Registar a minha empresa',
    features: [
      ['Registo com o número fiscal do seu país', 'Aceitamos qualquer formato de número fiscal ou registo comercial — não precisa de NIF cabo-verdiano.'],
      ['Pagamento por Visa / Mastercard', 'Pague online com cartão internacional. Recibo emitido com o seu país e número fiscal.'],
      ['Candidatos que falam PT / EN / FR', 'Indique os idiomas exigidos em cada vaga e filtre os perfis certos.'],
      ['Fuso horário próximo da Europa (UTC-1)', 'Apenas 1 a 2 horas de diferença para Lisboa, Paris ou Amesterdão.'],
    ],
    pricesTitle: 'Preços',
    pricesNote: 'Cobrança em escudos cabo-verdianos (CVE). Valores em euros aproximados (1 € ≈ 110,265 CVE).',
    note: 'A relação contratual (contrato ou prestação de serviços) é da responsabilidade da empresa e do profissional.',
  },
  en: {
    eyebrow: 'International and diaspora companies',
    title: 'Hire Cape Verdean talent remotely.',
    lead: 'Post remote jobs or freelance contracts and receive applications from professionals based in Cape Verde.',
    cta: 'Register my company',
    features: [
      ['Register with your own country\'s tax number', 'Any tax ID or company registration format is accepted — no Cape Verdean NIF required.'],
      ['Pay by Visa / Mastercard', 'Pay online with an international card. Receipts show your country and tax number.'],
      ['Candidates who speak PT / EN / FR', 'Set the required languages for each job and reach the right profiles.'],
      ['Time zone close to Europe (UTC-1)', 'Only 1 to 2 hours from Lisbon, Paris or Amsterdam.'],
    ],
    pricesTitle: 'Pricing',
    pricesNote: 'Charged in Cape Verdean escudos (CVE). Euro amounts are approximate (€1 ≈ 110.265 CVE).',
    note: 'The contractual relationship (employment contract or service agreement) is the responsibility of the company and the professional.',
  },
  fr: {
    eyebrow: 'Entreprises internationales et de la diaspora',
    title: 'Recrutez des talents cap-verdiens à distance.',
    lead: 'Publiez des offres en télétravail ou en prestation de services et recevez des candidatures de professionnels basés au Cap-Vert.',
    cta: 'Inscrire mon entreprise',
    features: [
      ['Inscription avec le numéro fiscal de votre pays', 'Tout format de numéro fiscal ou d\'immatriculation est accepté — pas besoin de NIF cap-verdien.'],
      ['Paiement par Visa / Mastercard', 'Payez en ligne par carte internationale. Le reçu indique votre pays et votre numéro fiscal.'],
      ['Candidats parlant PT / EN / FR', 'Indiquez les langues requises pour chaque offre et touchez les bons profils.'],
      ['Fuseau horaire proche de l\'Europe (UTC-1)', 'Seulement 1 à 2 heures de décalage avec Lisbonne, Paris ou Amsterdam.'],
    ],
    pricesTitle: 'Tarifs',
    pricesNote: 'Facturation en escudos cap-verdiens (CVE). Montants en euros approximatifs (1 € ≈ 110,265 CVE).',
    note: 'La relation contractuelle (contrat de travail ou prestation de services) relève de la responsabilité de l\'entreprise et du professionnel.',
  },
};
const ICONS = [Building2, CreditCard, Languages, Clock];

export default function EmpresasInternacionais() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = (i18n.language || 'pt').slice(0, 2);
  const c = COPY[lang] || COPY.pt;
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    plansAPI.getPlans().then((r) => setPlans((r.data || []).filter((p) => p.price_cve > 0))).catch(() => setPlans([]));
  }, []);

  return (
    <main className="bg-slate-50 min-h-screen" data-testid="intl-companies-page">
      <Helmet><title>{`${c.title} · Trabadju`}</title></Helmet>
      <section className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-cta mb-5">
            <Globe2 className="h-4 w-4" aria-hidden="true" />{c.eyebrow}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl leading-[1.05] text-white" data-testid="intl-title">{c.title}</h1>
          <p className="mt-6 max-w-xl text-base text-blue-100 leading-relaxed">{c.lead}</p>
          <button
            type="button"
            onClick={() => navigate('/register?intl=1&country=PT')}
            className="group mt-8 inline-flex items-center gap-2 h-12 px-6 bg-cta text-navy font-semibold rounded-lg hover:bg-amber-400 transition-colors"
            data-testid="intl-register-btn"
          >
            {c.cta}<ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
          </button>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid sm:grid-cols-2 gap-5" data-testid="intl-features">
        {c.features.map(([title, body], i) => {
          const Icon = ICONS[i];
          return (
            <div key={title} className="bg-white rounded-xl border border-slate-200 p-6 hover:border-slate-900 transition-colors" data-testid={`intl-feature-${i}`}>
              <Icon className="h-6 w-6 text-blue-700 mb-4" aria-hidden="true" />
              <h2 className="text-base md:text-lg font-bold text-slate-900">{title}</h2>
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{body}</p>
            </div>
          );
        })}
      </section>

      {plans.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-14" data-testid="intl-prices">
          <h2 className="text-base md:text-lg font-bold text-slate-900 mb-1">{c.pricesTitle}</h2>
          <p className="text-xs text-slate-500 mb-4">{c.pricesNote}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {plans.map((p) => (
              <div key={p.plan_type} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`intl-price-${p.plan_type}`}>
                <div className="text-sm font-semibold text-slate-700">{p[`name_${lang}`] || p.name_pt}</div>
                <div className="text-xl font-bold text-slate-900 mt-1">{p.price_cve.toLocaleString('pt-PT')} CVE</div>
                <EurHint cve={p.price_cve} force className="text-slate-500" testId={`intl-price-eur-${p.plan_type}`} />
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900" data-testid="intl-contract-note">
          <FileSignature className="h-5 w-5 flex-none mt-0.5" aria-hidden="true" />
          <p>{c.note}</p>
        </div>
      </section>
    </main>
  );
}
